import { useCallback, useMemo } from "react";
import Sidebar from "components/layout/sidebar/sidebar";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "components/shadcn/ui/card";
import { Button } from "components/shadcn/ui/button";
import { dashboardData } from "./dashboardData";
import { mixpanelCustomEvent } from "mixpanel/eventTriggers";
import { useAuth, useUser } from "@clerk/clerk-react";
import { MixpanelEventName } from "mixpanel/types";
import { useRoleUpdater } from "hook/useRoleUpdater";
import { Role } from "types";

const Dashboard = () => {
  const { user } = useUser();
  const { isSignedIn } = useAuth();

  const mixpanelProps = useMemo(
    () => ({
      $name: `${user?.fullName}`,
      $email: user?.emailAddresses[0]?.emailAddress,
    }),
    [user?.fullName, user?.emailAddresses]
  );

  const { updateRoles } = useRoleUpdater({
    user,
    isSignedIn: isSignedIn ?? false,
    mixpanelProps,
  });
  const handleLaunch = useCallback(
    async (title: string, url?: string, role?: string) => {
      if (!url) return;

      mixpanelCustomEvent({
        mixpanelProps: {
          ...mixpanelProps,
          buttonName: title,
        },
        id: user?.id?.toString() || "",
        eventName: MixpanelEventName.userClickedonButtonFromDashboardPage,
      });

      await updateRoles(role as Role | undefined);

      setTimeout(() => {
        window.location.href = url;
      }, 500);
    },
    [mixpanelProps, updateRoles, user?.id]
  );

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} />

      <main className="flex-1 px-6 py-12">
        <section className="flex flex-col items-center justify-center text-center mb-16">
          <h1 className="text-4xl font-bold">Welcome to BizCrunch</h1>
          <p className="text-gray-500 mt-2 text-lg">
            Choose where you want to work today.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboardData.map(
            ({ title, description, icon, url, color, role }) => (
              <Card
                key={title}
                className="hover:shadow-lg transition-shadow duration-300 border border-gray-200 bg-white"
              >
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className={`text-2xl ${color}`}>{icon}</div>
                    <CardTitle className="text-xl">{title}</CardTitle>
                  </div>
                  <CardDescription className="pt-2">
                    {description}
                  </CardDescription>
                </CardHeader>

                <CardContent />

                <CardFooter>
                  <Button
                    variant="primary"
                    className="w-full"
                    aria-label={`Launch ${title}`}
                    onClick={() => handleLaunch(title, url, role)}
                  >
                    Open {title}
                  </Button>
                </CardFooter>
              </Card>
            )
          )}
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
